import React, { useState, useEffect } from 'react';
import SettingsSidebar from './settings/SettingsSidebar';

// Operations Section
import Practice from './settings/operations/Practice';
import BusinessFiles from './settings/operations/BusinessFiles';
import PlanInfo from './settings/operations/PlanInfo';
import DataExport from './settings/operations/DataExport';
import DataImport from './settings/operations/DataImport';
import DataRetention from './settings/operations/DataRetention';
import TeamMembers from './settings/operations/TeamMembers';
import RolesAccess from './settings/operations/RolesAccess';
import NotificationPreferences from './NotificationPreferences';
import AccountActivity from './settings/operations/AccountActivity';

// Billing Section
import ClientBillingInsurance from './settings/billing/ClientBillingInsurance';
import ClientBillingDocuments from './settings/billing/ClientBillingDocuments';
import InsuranceBilling from './settings/billing/InsuranceBilling';
import Payers from './settings/billing/Payers';
import OnlinePayments from './settings/billing/OnlinePayments';
import ServicesProducts from './settings/billing/ServicesProducts';

// Client Care Section
import Documentation from './settings/clientcare/Documentation';
import ClientNotifications from './settings/clientcare/ClientNotifications';
import CompanyFormSettings from './settings/clientcare/CompanyFormSettings';
import CustomMeasureBuilder from './settings/clientcare/CustomMeasureBuilder';

// Dashboard tabs reused in Settings
import HRTab from './dashboard/provider/HRTab';
import MarketingTab from './dashboard/provider/MarketingTab';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

interface SettingsPageProps {
  onNavigateToTemplateLibrary?: () => void;
}

const SettingsPageNew: React.FC<SettingsPageProps> = ({ onNavigateToTemplateLibrary }) => {
  const { user, userProfile, roles } = useAuth();
  const [activeSection, setActiveSection] = useState<string>('practice-details');
  const [practiceOwnerId, setPracticeOwnerId] = useState<string | null>(null);

  // Resolve practice owner ID for team members
  useEffect(() => {
    if (!user?.uid) return;
    (async () => {
      try {
        const q = query(collection(db, 'team_members'), where('authUid', '==', user.uid));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const ownerIdFromTeam = snap.docs[0].data().practiceOwnerId;
          if (ownerIdFromTeam && ownerIdFromTeam !== user.uid) {
            setPracticeOwnerId(ownerIdFromTeam);
            return;
          }
        }
        setPracticeOwnerId(user.uid);
      } catch { setPracticeOwnerId(user.uid); }
    })();
  }, [user?.uid]);

  // Render the appropriate content component based on active section
  const renderContent = () => {
    switch (activeSection) {
      // Operations - Practice sub-items
      case 'practice-details':
        return <Practice />;
      case 'business-files':
        return <BusinessFiles />;
      case 'plan-info':
        return <PlanInfo />;
      case 'data-export':
        return <DataExport />;
      case 'data-import':
        return <DataImport />;
      case 'data-retention':
        return <DataRetention />;

      // Operations - Other items
      case 'team':
        return <TeamMembers />;
      case 'roles-access':
        return <RolesAccess />;
      case 'account-activity':
        return <AccountActivity />;
      case 'notifications':
        return <NotificationPreferences />;

      // Billing - Client billing and insurance sub-items
      case 'client-billing-documents':
        return <ClientBillingDocuments />;
      case 'insurance-billing':
        return <InsuranceBilling />;
      case 'payers':
        return <Payers />;

      // Billing - Other items
      case 'client-billing':
        return <ClientBillingInsurance />;
      case 'payment-processing':
        return <OnlinePayments />;
      case 'services-products':
        return <ServicesProducts />;

      // Client Care
      case 'documentation':
        return <Documentation />;
      case 'custom-measures':
        return <CustomMeasureBuilder practiceOwnerId={practiceOwnerId || user?.uid || ''} />;
      case 'client-notifications':
        return <ClientNotifications />;
      case 'form-settings':
        return <CompanyFormSettings />;

      // HR Management
      case 'hr-management':
        return <HRTab providerId={practiceOwnerId || user?.uid || ''} providerName={userProfile?.displayName || ''} />;

      // Marketing
      case 'marketing-profile':
      case 'marketing-website':
        return <MarketingTab />;

      default:
        return <Practice />;
    }
  };

  return (
    <div style={{
      display: 'flex',
      height: '100%',
      backgroundColor: '#0A2540',
      overflow: 'hidden'
    }}>
      {/* Sidebar Navigation */}
      <SettingsSidebar
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        userRoles={roles}
      />

      {/* Main Content Area */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        backgroundColor: '#0A2540'
      }}>
        {renderContent()}
      </div>
    </div>
  );
};

export default SettingsPageNew;
